import { ReflectUtils } from "@dooboostore/core";
import { ensureInit } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
const AROUND_METADATA_KEY = Symbol.for("simple-web-component:around");
const resolveHelperHostSet = (inst) => {
  try {
    ensureInit(inst);
    return SwcUtils.getHelperAndHostSet(inst);
  } catch {
    return { $this: inst };
  }
};
function around(options = {}) {
  return (target, propertyKey, descriptor) => {
    if (descriptor && typeof descriptor.value === "function") {
      const original = descriptor.value;
      const runAfter = function(hhs, res) {
        if (!options.after) return res;
        const run = (v) => options.after.call(this, hhs, v);
        return res && typeof res.then === "function" ? res.then(run) : run(res);
      };
      descriptor.value = function(...args) {
        const hhs = resolveHelperHostSet(this);
        const self = this;
        const callOriginal = (finalArgs) => runAfter.call(self, hhs, original.apply(self, finalArgs));
        const runBody = () => {
          if (options.before) {
            const mapped = options.before.call(self, hhs, args);
            if (mapped && typeof mapped.then === "function") {
              return mapped.then((m) => {
                if (Array.isArray(m)) return callOriginal(m);
                if (m !== void 0) return callOriginal([m]);
                return callOriginal(args);
              });
            }
            if (Array.isArray(mapped)) return callOriginal(mapped);
            if (mapped !== void 0) return callOriginal([mapped]);
          }
          return callOriginal(args);
        };
        if (!options.finally) return runBody();
        const afterFin = (settle, ctx) => {
          const f = options.finally.call(self, hhs, ctx);
          return f && typeof f.then === "function" ? f.then(settle) : settle();
        };
        let out;
        try {
          out = runBody();
        } catch (e) {
          return afterFin(() => {
            throw e;
          }, { args, error: e });
        }
        if (out && typeof out.then === "function") {
          return out.then(
            (v) => afterFin(() => v, { args, result: v }),
            (e) => afterFin(() => {
              throw e;
            }, { args, error: e })
          );
        }
        return afterFin(() => out, { args, result: out });
      };
      return descriptor;
    }
    const constructor = target.constructor;
    let aroundList = ReflectUtils.getOwnMetadata(AROUND_METADATA_KEY, constructor);
    if (!aroundList) {
      aroundList = [];
      ReflectUtils.defineMetadata(AROUND_METADATA_KEY, aroundList, constructor);
    }
    aroundList.push({ propertyKey });
    const storageKey = Symbol(`around:${String(propertyKey)}`);
    const getFn = options.get;
    const setFn = options.set;
    Object.defineProperty(target, propertyKey, {
      get() {
        const own = Object.getOwnPropertyDescriptor(this, propertyKey);
        if (own && "value" in own) {
          delete this[propertyKey];
          this[storageKey] = setFn ? setFn.call(this, resolveHelperHostSet(this), own.value) : own.value;
        }
        const raw = this[storageKey];
        if (!getFn) return raw;
        const mapped = getFn.call(this, resolveHelperHostSet(this), raw);
        return mapped === void 0 ? raw : mapped;
      },
      set(value) {
        this[storageKey] = setFn ? setFn.call(this, resolveHelperHostSet(this), value) : value;
      },
      enumerable: true,
      configurable: true
    });
  };
}
const findAllAroundMetadata = (target) => {
  const constructor = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(AROUND_METADATA_KEY, constructor).flat();
};
export {
  AROUND_METADATA_KEY,
  around,
  findAllAroundMetadata
};
//# sourceMappingURL=around.js.map
